import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { toast } from 'react-toastify';
import posthog from '@libs/posthog';
import { useAuth } from '@contexts/AuthContext';
import { useAppSelectors } from '@stores/app/appStore';
import { POSTHOG_SLACK_CHANNEL_POPUP_SURVEY_ID } from '@config/posthogSurveys';
import SlackIcon from '../SlackCommunityPopup/SlackIcon';

const TOAST_ID = 'slack-channel-toast';
// Long enough to read, short enough to feel like a toast.
const TOAST_DURATION_MS = 15000;

/**
 * One-time bottom-center toast pointing users to their dedicated Slack
 * support channel. Fires the first time the user lands cleanly on the
 * root page after the paywall — no subscription modal blocking. The
 * welcome modal sequencing in ProtectedLayout guarantees the welcome
 * step is already done by the time the paywall is dismissable, so we
 * only need to gate on isSubscriptionModalOpen.
 *
 * PostHog survey suppression handles "first time per user" across
 * devices.
 */
const SlackChannelToast = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const isSubscriptionModalOpen = useAppSelectors.isSubscriptionModalOpen();
  const shownRef = useRef(false);
  // Set when the user clicks the success action so the timer/X path
  // doesn't double-fire 'survey dismissed'.
  const respondedRef = useRef(false);

  const channelName = currentUser?.slack_onboarding_channel;
  const isOnRoot = location.pathname === '/';
  const ready =
    isOnRoot &&
    !isSubscriptionModalOpen &&
    !!currentUser?.subscription &&
    currentUser?.onboard_completed === true &&
    !!channelName;

  useEffect(() => {
    if (!ready || shownRef.current) return;

    posthog.getActiveMatchingSurveys((surveys) => {
      const survey = surveys.find(
        (s: { id: string }) =>
          s.id === POSTHOG_SLACK_CHANNEL_POPUP_SURVEY_ID,
      );
      if (!survey || shownRef.current) return;
      shownRef.current = true;

      posthog.capture('survey shown', {
        $survey_id: POSTHOG_SLACK_CHANNEL_POPUP_SURVEY_ID,
      });

      const handleCheckEmail = () => {
        respondedRef.current = true;
        posthog.capture('survey sent', {
          $survey_id: POSTHOG_SLACK_CHANNEL_POPUP_SURVEY_ID,
          $survey_response: 'check_email',
        });
        window.open(
          'https://mail.google.com',
          '_blank',
          'noopener,noreferrer',
        );
        toast.dismiss(TOAST_ID);
      };

      toast(
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <SlackIcon size={20} />
            <span className="font-semibold text-gray-900">
              Chat with an expert in Slack for free
            </span>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed m-0">
            We've created <strong>#{channelName}</strong> — a direct line to
            our team. Check your email for the Slack Connect invite to join.
          </p>
          <button
            className="self-start mt-1 px-4 py-2 border-none rounded-lg bg-leo-primary text-white text-sm font-semibold cursor-pointer transition-opacity hover:opacity-90"
            onClick={handleCheckEmail}
          >
            Check your email
          </button>
        </div>,
        {
          toastId: TOAST_ID,
          position: 'bottom-right',
          autoClose: TOAST_DURATION_MS,
          closeOnClick: false,
          draggable: false,
          onClose: () => {
            if (respondedRef.current) return;
            posthog.capture('survey dismissed', {
              $survey_id: POSTHOG_SLACK_CHANNEL_POPUP_SURVEY_ID,
            });
          },
        },
      );
    }, true);
  }, [ready, channelName]);

  return null;
};

export default SlackChannelToast;
